import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import * as PhosphorIcons from '@phosphor-icons/react';

const { Warning, CreditCard, ArrowRight, SignOut, MusicNotes } = PhosphorIcons;

const RAZORPAY_KEY_ID = 'rzp_test_Sf25LOdqLnJ7jL';

const Expired = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleResubscribe = async () => {
    setLoading(true);
    try {
      const orderRes = await apiClient.post('/subscription/create-order', { users: 2, locations: 1 });
      const { order_id, amount, key_id } = orderRes.data;

      const options = {
        key: key_id || RAZORPAY_KEY_ID,
        amount: amount,
        currency: 'INR',
        name: 'MusicSync',
        description: 'Monthly Plan - All Features',
        order_id: order_id,
        handler: async (response) => {
          try {
            await apiClient.post('/subscription/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Subscription activated! Redirecting...');
            setTimeout(() => { window.location.href = '/dashboard'; }, 1500);
          } catch (err) { toast.error('Payment verification failed'); }
        },
        prefill: { email: user?.email },
        theme: { color: '#2563eb' },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) { toast.error('Failed to create order'); }
    finally { setLoading(false); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-[#f5f0e8] flex items-center justify-center p-6" data-testid="expired-page">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center mx-auto mb-4">
            <MusicNotes size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#2d2a26] mb-2">MusicSync</h1>
        </div>

        <div className="bg-white rounded-2xl border border-[#d9d3c5] p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <Warning size={24} weight="bold" className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#2d2a26]">Subscription Expired</h2>
              <p className="text-sm text-[#8a8477]">Your access has been paused</p>
            </div>
          </div>

          <p className="text-[#6b665c] text-sm mb-6">
            Your subscription or trial has ended. Resubscribe to continue managing your in-store music across all locations.
          </p>

          <div className="bg-[#f5f0e8] rounded-xl p-5 mb-6 border border-[#e8e3d8]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#2d2a26]">MusicSync Pro</h3>
              <div className="text-right">
                <span className="text-2xl font-bold text-blue-600">Rs. 499</span>
                <span className="text-sm text-[#8a8477]">/month</span>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[#6b665c]">
              <li className="flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" />All features included</li>
              <li className="flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" />2 users (1 admin + 1 kiosk)</li>
              <li className="flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" />1 location</li>
              <li className="flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" />Add more: Rs. 299/location, Rs. 99/user</li>
            </ul>
          </div>

          <Button onClick={handleResubscribe} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0 h-12 text-base font-medium" data-testid="resubscribe-button">
            <CreditCard size={20} className="mr-2" />{loading ? 'Processing...' : 'Resubscribe Now — Rs. 499/mo'}
          </Button>

          <Button onClick={handleLogout} className="w-full mt-3 bg-transparent hover:bg-[#f5f0e8] text-[#8a8477] border border-[#d9d3c5]" data-testid="expired-logout-button">
            <SignOut size={18} className="mr-2" />Logout
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Expired;
